import React, { useEffect, useRef } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import "../DataTable/DataTable.css";
import {
  Badge,
  Checkbox,
  Grid,
  InputAdornment,
  Pagination,
  TextField,
  Tooltip,
} from "@mui/material";
import ToggleSwitch from "../../../common/Switch/ToggleSwitch";
import { toast } from "react-toastify";
import { COLUMN_TYPES, decision, status } from "../../../constants/common";
import AddEditRoleModal from "../../ManageRoles/AddEditRoleModal";
import moment from "moment";
import ThreeDotMenu from "../../../common/ThreeDotMenu/ThreeDotMenu";
import { SystemUsersEnum } from "../../../constants/SystemUsers";
import {
  ContentPublishStatus,
  DocumentType,
} from "../../Distributors/constants";
import { UNBLOCK_USER } from "../../../constants/api-constants";
import ApiServices from "../../../services/ApiServices";
import { useLoading } from "../../../hooks/useLoader";
import Confirmation from "../../../common/Confirmation/Confirmation";
import { useAuth } from "../../../hooks/useAuth";
import { PermissionEnum } from "../../../constants/permissionService";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PropTypes from "prop-types";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import AddShoppingCartOutlinedIcon from "@mui/icons-material/AddShoppingCartOutlined";
import SettingIcon from "../../../common/Icons/SettingIcon";
import { isSuperAdminOrAdminRole } from "../../../utilities/UserRoleUtils";

const EllipsisText = ({ text, maxWidth = 200 }) => {
  const textRef = useRef(null);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const normalizedText =
    text === null || text === undefined ? "" : String(text);

  useEffect(() => {
    const el = textRef.current;
    if (!el) {
      return;
    }

    const checkOverflow = () => {
      setShowTooltip(el.scrollWidth > el.clientWidth);
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);

    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, [normalizedText, maxWidth]);

  return (
    <span
      ref={textRef}
      title={showTooltip ? normalizedText : ""}
      style={{
        maxWidth: maxWidth,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "inline-block",
        verticalAlign: "bottom",
      }}
    >
      {normalizedText}
    </span>
  );
};

EllipsisText.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxWidth: PropTypes.number,
};

const getCellTextMaxWidth = (column) => {
  const configured = Number(column?.textOverflowWidth);
  if (Number.isFinite(configured) && configured > 0) {
    return configured;
  }
  const width = Number(column?.width);
  if (Number.isFinite(width) && width > 0) {
    return width;
  }
  return 200;
};

const joinClassNames = (...classNames) =>
  classNames.filter(Boolean).join(" ").trim();

const getStickyCellStyle = (column) => {
  if (!column?.isFixed) {
    return {};
  }

  const fixedToLeft =
    column.fixedSide === "left" || column.leftPadding !== undefined;
  const offsetKey = fixedToLeft ? "left" : "right";
  const offsetValue = fixedToLeft ? column.leftPadding : column.rightPadding;

  return {
    minWidth: column.width + "px",
    maxWidth: column.width + "px",
    [offsetKey]: (offsetValue || 0) + "px",
  };
};

const applyStickyCellProps = (cellElement, column) => {
  if (!column?.isFixed || !React.isValidElement(cellElement)) {
    return cellElement;
  }

  return React.cloneElement(cellElement, {
    className: joinClassNames(cellElement.props.className, "sticky-col"),
    style: {
      ...cellElement.props.style,
      ...getStickyCellStyle(column),
    },
  });
};

const DataTable = (props) => {
  const isdistributorContent = window.location.pathname.includes("distributor");
  const isenterpriseContent =
    window.location.pathname.includes("enterprisegroup");
  const isAccessDetails = window.location.pathname.includes("access-details");
  const isVendorDetails = window.location.pathname.includes("vendor/");
  const isManageDocument =
    window.location.pathname.includes("manage-documents");
  const ent = window.location.pathname.endsWith("content");
  const entr = window.location.pathname.endsWith("catalog");
  const entrs = window.location.pathname.endsWith("bundle");
  const toggleContent = window.location.pathname.endsWith(
    "content/ManageSubscribers",
  );
  const toggleCatalog = window.location.pathname.endsWith(
    "catalog/ManageSubscribers",
  );
  const toggleBundle = window.location.pathname.endsWith(
    "bundle/ManageSubscribers",
  );
  const userSelectedRoleId = Number(localStorage.getItem("selectedRole"));
  const isPathEnt = ent;
  const isPathEntr = entr;
  const isPathEntrs = entrs;
  const isPathContentManageSubscribers = toggleContent;
  const isPathCatalogManageSubscribers = toggleCatalog;
  const isPathBundleManageSubscribers = props.showToggle || toggleBundle;
  const isPathVendorService = props.showServiceIcon;
  const auth = useAuth();
  const tableContainerRef = useRef(null);

  const {
    columns,
    showFavorite,
    showMenu,
    showView,
    showEdit,
    uniqueKey,
    onView,
    onEdit,
    onReject,
    actionHandler,
    adminDetails,
    showDeleteDocument,
  } = props;
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const contentSubscriberFieldName = "contentSubscriberStatus";
  const contentAssociationFieldName = "contentAssociationStatus";
  const [sortColumn, setSortColumn] = React.useState(""); // To track the currently sorted column
  const [sortOrder, setSortOrder] = React.useState(""); // To track the sorting order (ascending or descending)
  const [openUnBlockUserPopup, setOpenUnBlockUserPopup] = React.useState(false);
  const { showLoader } = useLoading();
  const [ids, setIds] = React.useState(null);
  const scrollBarReset = JSON.stringify(
    props.adminTable?.map(({ isChecked, ...rest }) => rest),
  );

  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [scrollBarReset]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      // If the same column is clicked again, reverse the sorting order
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // If a different column is clicked, set it as the new sorting column in ascending order
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const getSortImage = (column) => {
    if (sortColumn === column) {
      if (sortOrder === "asc") {
        return "/images/Ascending@2x-1.png";
      } else {
        return "/images/Descending@2x-1.png";
      }
    } else {
      return "/images/unsorted.png";
    }
  };
  /*istanbul ignore next */
  const onUnBlockUserHandler = async () => {
    try {
      showLoader(true);
      const res = await ApiServices.httpPost(UNBLOCK_USER, {
        userId: ids,
      });
      showLoader(false);
      if (res.responseStatus?.isSuccess) {
        toast.success(res.responseStatus.description);
        props.success(true);
        setOpenUnBlockUserPopup(false);
      } else {
        toast.error(res.responseStatus.error);
        props.success(false);
      }
    } catch (e) {
      showLoader(false);
      toast.error("Internal server error");
    }
  };
  /*istanbul ignore next */
  const renderTableCell = (column, res) => {
    let cellElement;
    switch (column.type) {
      case COLUMN_TYPES.DATE: {
        cellElement = columnTypeDate(res, column);
        break;
      }
      case COLUMN_TYPES.EMAIL: {
        cellElement = columnTypeEmail(res, column);
        break;
      }
      case COLUMN_TYPES.MULTI_VALUE: {
        cellElement = columnTypeMultiValue(res, column);
        break;
      }
      case COLUMN_TYPES.UNBLOCK_TOGGLE: {
        cellElement = columnTypeUnblockToggle(
          res,
          props,
          setOpenUnBlockUserPopup,
          setIds,
          column,
        );
        break;
      }
      case COLUMN_TYPES.STATUS_CHANGE: {
        let dataObj2 = {
          auth: auth,
          isPathEnt: isPathEnt,
          isPathEntr: isPathEntr,
          isPathEntrs: isPathEntrs,
          isPathContentManageSubscribers: isPathContentManageSubscribers,
          isPathCatalogManageSubscribers: isPathCatalogManageSubscribers,
          isPathBundleManageSubscribers: isPathBundleManageSubscribers,
          column: column,
          props: props,
          res: res,
          contentSubscriberFieldName: contentSubscriberFieldName,
          contentAssociationFieldName: contentAssociationFieldName,
          uniqueKey: uniqueKey,
          handleOpen: handleOpen,
          open: open,
        };
        cellElement = columnTypeStatusChange(dataObj2);
        break;
      }
      case COLUMN_TYPES.ACTIVE_INACTIVE_LABEL: {
        cellElement = columnTypeActiveInactiveLabel(res, column);
        break;
      }
      case COLUMN_TYPES.BOOLEAN: {
        const fieldName = res[column.fieldName];
        let resultString;
        switch (fieldName) {
          case ContentPublishStatus.DRAFT:
            resultString = "Draft";
            break;
          case ContentPublishStatus.PUBLISHED:
            resultString = "Published";
            break;
          case ContentPublishStatus.MODIFIED:
            resultString = "Modified";
            break;
          case true:
            resultString = column.YesLabelText;
            break;
          case false:
            resultString = column.NoLabelText;
            break;
          default:
            resultString = "";
        }
        cellElement = (
          <TableCell>
            <EllipsisText
              text={resultString}
              maxWidth={getCellTextMaxWidth(column)}
            />
          </TableCell>
        );
        break;
      }
      case COLUMN_TYPES.ACTION: {
        // Allow dynamic disabling via function prop
        let disableSettingAction;
        if (typeof props.disableSettingAction === "function") {
          disableSettingAction = props.disableSettingAction(res);
        } else {
          disableSettingAction =
            res.subscriptionStatus === 0 ||
            res.subscriptionStatusText === "Not Purchased";
        }
        let dataObj = {
          showEdit: showEdit,
          props: props,
          res: res,
          adminDetails: adminDetails,
          uniqueKey: uniqueKey,
          userSelectedRoleId: userSelectedRoleId,
          onEdit: onEdit,
          showView: showView,
          onView: onView,
          showFavorite: showFavorite,
          showMenu: showMenu,
          column: column,
          auth: auth,
          actionHandler: actionHandler,
          showDeleteDocument: showDeleteDocument,
          permissions: props.permissions,
          isPathVendorService: isPathVendorService,
          showSettingAction: props.showSettingAction,
          onSettings: props.onSettings,
          disableSettingAction,
          filterMenuItems: props.filterMenuItems,
        };
        cellElement = columnTypeAction(dataObj);
        break;
      }
      case COLUMN_TYPES.DOCTYPE: {
        cellElement = columnTypeDocType(res, column);
        break;
      }
      case COLUMN_TYPES.STYLED_BASIC: {
        let dataObj4 = {
          showView: showView,
          onReject: onReject,
        };
        cellElement = columnTypeStyledBasicWithViewIcon(dataObj4, res, column);
        break;
      }
      case COLUMN_TYPES.ENUM: {
        cellElement = (
          <TableCell>
            <EllipsisText
              text={column.mapping[res[column.fieldName]]}
              maxWidth={getCellTextMaxWidth(column)}
            />
          </TableCell>
        );
        break;
      }
      case COLUMN_TYPES.DESCRIPTION: {
        let description = res[column.fieldName];
        cellElement = (
          <TableCell className="truncated-description">
            <EllipsisText
              text={description}
              maxWidth={getCellTextMaxWidth(column)}
            />
          </TableCell>
        );
        break;
      }
      default: {
        cellElement = (
          <TableCell>
            <EllipsisText
              text={res[column.fieldName]}
              maxWidth={getCellTextMaxWidth(column)}
            />
          </TableCell>
        );
        break;
      }
    }
    return applyStickyCellProps(cellElement, column);
  };
  const checkMngDoc = isManageDocument ? "resp-table-managedoc" : "resp-table";
  const tableClassByScreen =
    isdistributorContent ||
    isenterpriseContent ||
    isAccessDetails ||
    isVendorDetails
      ? "resp-table-dist-content"
      : checkMngDoc;
  return (
    <div className="admin-table">
      <Confirmation
        open={openUnBlockUserPopup}
        handleClose={() => {
          setOpenUnBlockUserPopup(false);
        }}
        title="Are you sure you want to unlock the account?"
        onCancel={() => {
          setOpenUnBlockUserPopup(false);
          setIds(null);
        }}
        onConfirm={() => {
          onUnBlockUserHandler();
        }}
        cancelButtonLabel="No"
        confirmButtonLabel="Yes"
      />
      <TableContainer component={Paper} className="table-container">
        <div className="table-content">
          {!props.paginationNotRequired && (
            <p>
              <span className="mr-2">Show</span>
              <select
                onChange={(e) => props.onPageSizeChange(e.target.value)}
                className="select"
                id="cars"
                name="cars"
                data-testid="pageSizeCombo"
                value={props.query?.PageSize || 10}
              >
                {(props.pageSizeOptions || [10, 50, 100]).map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span className="ml-2">entries</span>
            </p>
          )}
          {!props.serachBarNotRequired && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginLeft: "auto",
              }}
            >
              {props.showCartButton && (
                <button
                  type="button"
                  onClick={props.handlegoCart}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 4,
                    border: "1px solid #325c86ff",
                    background: "#669dd4ff",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Go to Cart
                </button>
              )}
              {props.showCart && (
                <button
                  type="button"
                  onClick={props.handleCartClick}
                  style={{
                    background: "#1aaefa",
                    border: "none",
                    padding: "10px 8px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    borderRadius: "4px",
                  }}
                  aria-label="Shopping Cart"
                >
                  <Badge
                    badgeContent={props.cartCount || 0}
                    showZero
                    sx={{
                      "& .MuiBadge-badge": {
                        backgroundColor: "rgba(255, 60, 63, 1)",
                        color: "#fff",
                      },
                    }}
                    overlap="circular"
                  >
                    <ShoppingCartOutlinedIcon
                      style={{ color: "#fff", fontSize: "24px" }}
                    />
                  </Badge>
                  <span style={{ paddingLeft: "8px", color: "#fff" }}>
                    {" "}
                    Cart
                  </span>
                </button>
              )}
              <div className="table-search-boxx">
                <TextField
                  className="table-search-box"
                  onChange={props.onSearch}
                  type="text"
                  placeholder="Search Everything..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <div className="search-icon-div">
                          <img
                            src="/images/search-icon.png"
                            alt="search icon"
                            name="search-icon"
                          />
                        </div>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div className={tableClassByScreen} ref={tableContainerRef}>
          <Table
            sx={{ minWidth: 650 }}
            aria-label="simple table"
            id={props.uniqueKey}
          >
            <TableHead>
              <TableRow>
                {!props.checkboxesNotRequired && (
                  <TableCell style={{ width: "56px" }}>
                    <div className="col-details">
                      <Checkbox
                        name="selectUser"
                        data-testid="select-all"
                        checked={
                          props.adminTable?.length > 0 &&
                          props.adminTable?.filter((user) => user.isChecked)
                            .length === props.adminTable?.length
                        }
                        onChange={(event) =>
                          props.onCheckboxChange(event, "all")
                        }
                        sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
                        style={{ paddingLeft: 0 }}
                        disableRipple
                      />
                    </div>
                  </TableCell>
                )}
                {columns?.map(
                  (column, index) =>
                    column.isSelected && (
                      <TableCell
                        key={column.fieldName || index}
                        className={column.isFixed ? "sticky-header" : ""}
                        style={
                          column.isFixed
                            ? {
                                ...getStickyCellStyle(column),
                                zIndex: 3,
                              }
                            : {}
                        }
                      >
                        <div className="col-details">
                          <h6
                            className="col-details-heading table-column"
                            style={{
                              maxWidth: column.width,
                              width: "auto",
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            <EllipsisText
                              text={column.label}
                              maxWidth={column.width}
                            />
                            {column.sort &&
                              column.fieldName !== "Add-ins Name" && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const sortField =
                                      column.sortBy || column.fieldName;
                                    handleSort(sortField);
                                    props.onSort(
                                      props.query.SortType,
                                      sortField,
                                    );
                                  }}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    padding: 0,
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                  }}
                                  aria-label={`Sort by ${column.label}`}
                                >
                                  <img
                                    className="sort-icon"
                                    data-testid={`sort-by-${column.fieldName}`}
                                    src={getSortImage(
                                      column.sortBy || column.fieldName,
                                    )}
                                    alt=""
                                    role="presentation"
                                  />
                                </button>
                              )}
                          </h6>
                        </div>
                      </TableCell>
                    ),
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {props?.adminTable?.map((res, index) => {
                return (
                  <TableRow
                    key={res[uniqueKey]}
                    className={
                      index % 2 === 0 ? "table-row-grey" : "table-row-white"
                    }
                    style={props.getRowStyle ? props.getRowStyle(res) : {}}
                  >
                    {!props.checkboxesNotRequired && (
                      <TableCell style={{ width: "56px" }}>
                        <Checkbox
                          name={`selectuser-${res[uniqueKey]}`}
                          data-testid={`select-${res[uniqueKey]}`}
                          onChange={(event) =>
                            props.onCheckboxChange(event, res[uniqueKey])
                          }
                          checked={res.isChecked || false}
                          sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
                          style={{ paddingLeft: 0 }}
                          disableRipple
                        />
                      </TableCell>
                    )}

                    {columns.map((column) => {
                      return column.isSelected && renderTableCell(column, res);
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {props.customPagination ? (
          <div className="table-pagination">
            {props.currentPage > 1 && (
              <button
                onClick={props.handlePreviousPage}
                disabled={!props.hasPreviousPage}
              >
                <ArrowBackIosIcon style={{ paddingLeft: "8px" }} />
              </button>
            )}
            {props.currentPage >= 1 && props.hasNextPage && (
              <button
                onClick={props.handleNextPage}
                disabled={!props.hasNextPage}
              >
                <NavigateNextIcon />
              </button>
            )}
          </div>
        ) : (
          props.totalPages > 1 && (
            <div className="table-pagination">
              <Pagination
                page={props.page}
                onChange={props.handleChangePage}
                rowsPerPage={10}
                count={props.totalPages}
                variant="outlined"
                shape="rounded"
              />
            </div>
          )
        )}
      </TableContainer>
    </div>
  );
};
DataTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  showFavorite: PropTypes.bool,
  showMenu: PropTypes.bool,
  showView: PropTypes.bool,
  showEdit: PropTypes.bool,
  uniqueKey: PropTypes.string.isRequired,
  onView: PropTypes.func,
  onEdit: PropTypes.func,
  onReject: PropTypes.func,
  actionHandler: PropTypes.func,
  filterMenuItems: PropTypes.func,
  adminDetails: PropTypes.object,
  showDeleteDocument: PropTypes.bool,
  permissions: PropTypes.arrayOf(PropTypes.string),
  showToggle: PropTypes.bool,
  showServiceIcon: PropTypes.bool,
  onPageSizeChange: PropTypes.func,
  onSearch: PropTypes.func,
  onSort: PropTypes.func,
  query: PropTypes.object,
  adminTable: PropTypes.arrayOf(PropTypes.object),
  paginationNotRequired: PropTypes.bool,
  getRowStyle: PropTypes.func,
  serachBarNotRequired: PropTypes.bool,
  checkboxesNotRequired: PropTypes.bool,
  onCheckboxChange: PropTypes.func,
  customPagination: PropTypes.bool,
  currentPage: PropTypes.number,
  hasPreviousPage: PropTypes.bool,
  hasNextPage: PropTypes.bool,
  handlePreviousPage: PropTypes.func,
  handleNextPage: PropTypes.func,
  totalPages: PropTypes.number,
  page: PropTypes.number,
  handleChangePage: PropTypes.func,
  success: PropTypes.func,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onToggle: PropTypes.func,
  onDelete: PropTypes.func,
  onClick: PropTypes.func,
  onEditSuccess: PropTypes.func,
  featureListDetails: PropTypes.array,
  featureListDetails1: PropTypes.array,
  onClickService: PropTypes.func,
  showCartButton: PropTypes.bool,
  handlegoCart: PropTypes.func,
  showCart: PropTypes.bool,
  useCartIconForUnpurchased: PropTypes.bool,
  cartCount: PropTypes.number,
  handleCartClick: PropTypes.func,
  showSettingAction: PropTypes.bool,
  onSettings: PropTypes.func,
  disableSettingAction: PropTypes.bool,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
};
export default DataTable;

function columnTypeDocType(res, column) {
  const whenNotContent =
    res[column.fieldName] === DocumentType.CATALOG ? "Catalog" : "";
  let cellElement = (
    <TableCell>
      <EllipsisText
        text={
          res[column.fieldName] === DocumentType.CONTENT
            ? "Content"
            : whenNotContent
        }
        maxWidth={getCellTextMaxWidth(column)}
      />
    </TableCell>
  );
  return cellElement;
}
function classWhenMultiValue(column) {
  return column.isFixed ? " approval-roles-visible " : "";
}

/*istanbul ignore next */
function renderCartIcon(dataObj) {
  const isAddedInCart = !!dataObj.res.isAddedInCart;
  const IconComponent = isAddedInCart
    ? AddShoppingCartOutlinedIcon
    : ShoppingCartOutlinedIcon;
  const iconColor = isAddedInCart ? "#c41e3a" : "#1dcaff";
  const label = isAddedInCart ? "In Cart" : "Add to Cart";
  return (
    <button
      type="button"
      onClick={() => dataObj.onView(dataObj.res, dataObj.res.documentType)}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
      }}
      aria-label={label}
      title={label}
      data-testid={`CartIcon${dataObj.res[dataObj.uniqueKey]}`}
    >
      <IconComponent style={{ color: iconColor, fontSize: 20 }} />
    </button>
  );
}

/*istanbul ignore next */
function renderDisabledViewIcon(dataObj) {
  return (
    <img
      src="/images/view_disable.png"
      height={20}
      alt="View"
      title="View"
      style={{ cursor: "normal" }}
      data-testid={`ViewIcon${dataObj.res[dataObj.uniqueKey]}`}
    />
  );
}

/*istanbul ignore next */
function renderEnabledViewIcon(dataObj) {
  return (
    <button
      type="button"
      onClick={() => dataObj.onView(dataObj.res, dataObj.res.documentType)}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
      }}
      aria-label="View"
    >
      <img
        src="/images/view_enable.png"
        height={20}
        alt=""
        role="presentation"
        title="View"
        data-testid={`ViewIcon${dataObj.res[dataObj.uniqueKey]}`}
      />
    </button>
  );
}

/*istanbul ignore next */
function buildViewOrCartIcon(dataObj) {
  if (isSuperAdminOrAdminRole(dataObj.userSelectedRoleId)) {
    return renderEnabledViewIcon(dataObj);
  }

  const showCartInsteadOfView =
    dataObj.props.useCartIconForUnpurchased &&
    dataObj.res.subscriptionStatus !== 1;
  if (showCartInsteadOfView) {
    return renderCartIcon(dataObj);
  }
  const isDisabledView =
    dataObj.userSelectedRoleId === SystemUsersEnum.DISTRIBUTOR &&
    dataObj.res.publishStatus === ContentPublishStatus.PUBLISHED &&
    dataObj.res.status === 0;
  return isDisabledView
    ? renderDisabledViewIcon(dataObj)
    : renderEnabledViewIcon(dataObj);
}

/*istanbul ignore next */
function columnTypeAction(dataObj) {
  const whenNotRoleMgmt =
    dataObj.res.publishStatus === ContentPublishStatus.MODIFIED ||
    (dataObj.userSelectedRoleId === SystemUsersEnum.DISTRIBUTOR &&
      dataObj.res.publishStatus === ContentPublishStatus.PUBLISHED &&
      dataObj.res.status === 0) ? (
      <img
        src="/images/edit_disable.png"
        height={20}
        alt="Edit"
        title="Edit"
        style={{ cursor: "default" }}
        data-testid={`EditIcon${dataObj.res[dataObj.uniqueKey]}`}
      />
    ) : (
      dataObj.permissions?.includes(PermissionEnum.UPDATE) && (
        <button
          type="button"
          onClick={() => dataObj.onEdit(dataObj.res, dataObj.res.documentType)}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
          }}
          aria-label="Edit"
        >
          <img
            src="/images/edit_enable.png"
            height={20}
            alt=""
            role="presentation"
            title="Edit"
            data-testid={`EditIcon${dataObj.res[dataObj.uniqueKey]}`}
          />
        </button>
      )
    );
  const viewOrCartIcon = buildViewOrCartIcon(dataObj);

  const ifNotRoleMgmt = dataObj.res.isFavorite ? (
    <button
      type="button"
      onClick={() => dataObj.onFavorite?.(dataObj.res)}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
      }}
      aria-label="Favorite"
    >
      <img
        src="/images/favorite_enable.png"
        height={20}
        alt=""
        role="presentation"
        title="Favorite"
        data-testid={`favorite${dataObj.res[dataObj.uniqueKey]}`}
      />
    </button>
  ) : (
    <button
      type="button"
      onClick={() => dataObj.onFavorite?.(dataObj.res)}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
      }}
      aria-label="Favorite"
    >
      <img
        src="/images/unfavorite_enable.png"
        height={20}
        alt=""
        role="presentation"
        title="Favorite"
        data-testid={`favorite${dataObj.res[dataObj.uniqueKey]}`}
      />
    </button>
  );

  let cellElement = (
    <TableCell
      align="right"
      className={
        dataObj.column.isFixed ? "sticky-col action-cell" : "action-cell"
      }
      style={
        dataObj.column.isFixed
          ? {
              minWidth: dataObj.column.width + "px",
              maxWidth: dataObj.column.width + "px",
              right: dataObj.column.rightPadding + "px",
            }
          : {}
      }
    >
      <Grid container className="action-item-icons">
        {dataObj.showEdit && (
          <Grid item p={1}>
            {window.location.pathname === "/role-management" ? (
              <AddEditRoleModal
                isEventListner={true}
                featureListDetails={dataObj.props.featureListDetails}
                featureListDetails1={dataObj.props.featureListDetails1}
                isEditMode={true}
                isAdd={false}
                isView={true}
                onClick={dataObj.props.onClick}
                data={dataObj.res}
                onEditSuccess={dataObj.props.onEditSuccess}
                adminDetails={dataObj.adminDetails}
                testid={dataObj.res[dataObj.uniqueKey]}
                permissions={dataObj.permissions}
              />
            ) : (
              whenNotRoleMgmt
            )}
          </Grid>
        )}

        {dataObj.showView && (
          <Grid item p={1}>
            {window.location.pathname === "/role-management" ? (
              <AddEditRoleModal
                isEventListner={true}
                adminDetails={dataObj.adminDetails}
                featureListDetails={dataObj.props.featureListDetails}
                featureListDetails1={dataObj.props.featureListDetails1}
                isEditMode={false}
                onClick={dataObj.props.onClick}
                data={dataObj.res}
                onEditSuccess={dataObj.props.onEditSuccess}
                testid={dataObj.res[dataObj.uniqueKey]}
              />
            ) : (
              viewOrCartIcon
            )}
          </Grid>
        )}

        {dataObj.showFavorite && (
          <Grid item p={1}>
            {dataObj.showFavorite && dataObj.column.options && (
              <Grid item p={1}>
                {window.location.pathname === "/role-management" ? (
                  <AddEditRoleModal
                    isEventListner={true}
                    adminDetails={dataObj.adminDetails}
                    featureListDetails={dataObj.props.featureListDetails}
                    featureListDetails1={dataObj.props.featureListDetails1}
                    isEditMode={false}
                    onClick={dataObj.props.onClick}
                    data={dataObj.res}
                    onEditSuccess={dataObj.props.onEditSuccess}
                    testid={dataObj.res[dataObj.uniqueKey]}
                  />
                ) : (
                  ifNotRoleMgmt
                )}
              </Grid>
            )}
          </Grid>
        )}

        {dataObj.isPathVendorService && (
          <Grid item p={1}>
            <button
              type="button"
              onClick={() => dataObj.props.onClickService(dataObj.res)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
              aria-label="Service"
            >
              <img
                src="/images/Group serviceicon.png"
                height={20}
                alt=""
                role="presentation"
                title="Service"
                data-testid={`DownloadIcon${dataObj.res[dataObj.uniqueKey]}`}
              />
            </button>
          </Grid>
        )}

        {dataObj.column.options &&
          (dataObj.showFavorite || dataObj.showMenu) && (
            <Grid item ml={1}>
              {dataObj.auth.getRole() !== SystemUsersEnum.ENTERPRISE &&
                dataObj.permissions?.includes(PermissionEnum.UPDATE) && (
                  <ThreeDotMenu
                    actionHandler={dataObj.actionHandler}
                    options={(() => {
                      const mapped = dataObj.column?.options?.map((data) => ({
                        ...data,
                        disabled:
                          data.label === "Delete"
                            ? dataObj.res[data.status] ===
                              ContentPublishStatus.PUBLISHED
                            : dataObj.res[data.status],
                        res: dataObj.res,
                        isDistributor:
                          dataObj.userSelectedRoleId ===
                          SystemUsersEnum.DISTRIBUTOR,
                      }));
                      return dataObj.filterMenuItems
                        ? dataObj.filterMenuItems(mapped, dataObj.res)
                        : mapped;
                    })()}
                  />
                )}
            </Grid>
          )}

        {dataObj.showDeleteDocument && (
          <button
            type="button"
            onClick={() => dataObj.props.onDelete(dataObj.res)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              marginRight: "25px",
              cursor: "pointer",
            }}
            aria-label="Delete"
          >
            <img
              src="/images/delete_enable.png"
              height={20}
              alt=""
              role="presentation"
              title="Delete"
              data-testid={`DeleteDocument${dataObj.res[dataObj.uniqueKey]}`}
            />
          </button>
        )}

        {dataObj.showSettingAction && (
          <button
            type="button"
            onClick={() => dataObj.onSettings?.(dataObj.res)}
            style={{
              background: "none",
              border: "none",
              padding: "6px 12px 0 0",
              marginLeft: 8,
              cursor: "pointer",
            }}
            aria-label="Settings"
            disabled={dataObj.disableSettingAction}
          >
            <SettingIcon
              color={dataObj.disableSettingAction ? "#ccc" : "#666"}
              width={20}
              height={20}
              fontSize="8px"
            />
          </button>
        )}
      </Grid>
    </TableCell>
  );

  return cellElement;
}

/*istanbul ignore next */
function columnTypeActiveInactiveLabel(res, column) {
  let cellElement = (
    <TableCell>
      <EllipsisText
        text={res[column.fieldName] ? "Active" : "Inactive"}
        maxWidth={getCellTextMaxWidth(column)}
      />
    </TableCell>
  );
  return cellElement;
}

function columnTypeStatusChange(dataObj) {
  const whenNotToggleSwitch = dataObj.res[dataObj.column.fieldName]
    ? "Active"
    : "Inactive";
  const isDisabled = disableToggleSwitch(
    dataObj.column,
    dataObj.auth,
    dataObj.isPathEnt,
    dataObj.isPathEntr,
    dataObj.isPathEntrs,
    dataObj.res,
  );
  const disabledReason = isDisabled
    ? (dataObj.res.toggleDisabledReason ?? "")
    : "";
  const toggleEl = (
    <ToggleSwitch
      activeLabelText={dataObj.column.activeLabelText}
      inactiveLabelText={dataObj.column.inactiveLabelText}
      activeLabelColor={dataObj.column.activeLabelColor}
      inactiveLabelColor={dataObj.column.inactiveLabelColor}
      disabledLabelColor={dataObj.column.disabledLabelColor}
      success={dataObj.props.success}
      disabled={isDisabled}
      associate={associateToggleSwitch(
        dataObj.column,
        dataObj.contentSubscriberFieldName,
        dataObj.contentAssociationFieldName,
      )}
      notassociate={notAssociateToggleSwitch(
        dataObj.column,
        dataObj.contentSubscriberFieldName,
        dataObj.contentAssociationFieldName,
      )}
      id={`${dataObj.res[dataObj.uniqueKey]}${dataObj.column.fieldName}`}
      state={dataObj.res[dataObj.column.fieldName]}
      onChange={(e) => {
        onChangeToggleSwitch(
          dataObj.props,
          dataObj.column,
          dataObj.res,
          dataObj.contentAssociationFieldName,
          e,
          dataObj.uniqueKey,
          dataObj.handleOpen,
        );
      }}
      open={dataObj.open}
    />
  );
  let cellElement = (
    <TableCell
      className={dataObj.column.isFixed ? "sticky-col" : ""}
      style={
        dataObj.column.isFixed
          ? {
              minWidth: dataObj.column.width + "px",
              maxWidth: dataObj.column.width + "px",
              right: dataObj.column.rightPadding + "px",
            }
          : {}
      }
    >
      {(() => {
        const role = dataObj.auth.getRole();
        if (role === SystemUsersEnum.ENTERPRISE) return <></>;
        const showToggle = isToggleSwitch(
          dataObj.isPathEnt,
          dataObj.isPathEntr,
          dataObj.isPathEntrs,
          dataObj.isPathContentManageSubscribers,
          dataObj.isPathCatalogManageSubscribers,
          dataObj.isPathBundleManageSubscribers,
        );
        if (!showToggle) return <>{whenNotToggleSwitch}</>;
        if (disabledReason) {
          return (
            <Tooltip title={disabledReason} arrow>
              <span>{toggleEl}</span>
            </Tooltip>
          );
        }
        return toggleEl;
      })()}
    </TableCell>
  );
  return cellElement;
}
/*istanbul ignore next */
function onChangeToggleSwitch(
  props,
  column,
  res,
  contentAssociationFieldName,
  e,
  uniqueKey,
  handleOpen,
) {
  props.onToggle(
    column.fieldName,
    res.userId,
    column.fieldName === contentAssociationFieldName
      ? res.contentAssociationStatus
      : res.contentSubscriberStatus,
    e,
    res,
  );
  if (
    res[uniqueKey] &&
    res[uniqueKey] === props.currentUserId &&
    uniqueKey !== "contentId"
  ) {
    toast.error("You are not allowed to conduct this action.");
  }
  handleOpen();
}
/*istanbul ignore next */
function notAssociateToggleSwitch(
  column,
  contentSubscriberFieldName,
  contentAssociationFieldName,
) {
  return column.fieldName === contentSubscriberFieldName ||
    column.fieldName === contentAssociationFieldName
    ? decision[2]
    : status[2];
}

function associateToggleSwitch(
  column,
  contentSubscriberFieldName,
  contentAssociationFieldName,
) {
  return column.fieldName === contentSubscriberFieldName ||
    column.fieldName === contentAssociationFieldName
    ? decision[1]
    : status[1];
}
/*istanbul ignore next */
function disableToggleSwitch(
  column,
  auth,
  isPathEnt,
  isPathEntr,
  isPathEntrs,
  res,
) {
  return `${column.cssClass}` === "status-change" ||
    column.fieldName === status ||
    (auth.getRole() === SystemUsersEnum.ENTERPRISE &&
      (isPathEnt || isPathEntr || isPathEntrs)) ||
    res?.publishStatus === ContentPublishStatus.MODIFIED ||
    res?.disableToggleButton
    ? true
    : false;
}

function isToggleSwitch(
  isPathEnt,
  isPathEntr,
  isPathEntrs,
  isPathContentManageSubscribers,
  isPathCatalogManageSubscribers,
  isPathBundleManageSubscribers,
) {
  return (
    isPathEnt ||
    isPathEntr ||
    isPathEntrs ||
    isPathContentManageSubscribers ||
    isPathCatalogManageSubscribers ||
    isPathBundleManageSubscribers
  );
}
/*istanbul ignore next */
function columnTypeUnblockToggle(
  res,
  props,
  setOpenUnBlockUserPopup,
  setIds,
  column,
) {
  let cellElement = (
    <TableCell>
      <ToggleSwitch
        dataTestId="accountStatus"
        activeLabelText="Unlocked"
        inactiveLabelText="Locked"
        state={!res.isLocked}
        id={res.userId}
        onChange={() => {
          if (res.userId === props.currentUserId) {
            toast.error("You are not allowed to conduct this action.");
            return;
          }
          if (res.isLocked) {
            setOpenUnBlockUserPopup(true);
            setIds(res.userId);
          } else {
            //we can block user is not written on story so we are not doing any action on active/unblocked user
          }
        }}
      />
    </TableCell>
  );
  return cellElement;
}

function columnTypeMultiValue(res, column) {
  const multiValue = res[column.fieldName]?.flatMap((r) => r.name).join(", ");
  let cellElement = (
    <TableCell className={classWhenMultiValue(column)}>
      <EllipsisText text={multiValue} maxWidth={getCellTextMaxWidth(column)} />
    </TableCell>
  );
  return cellElement;
}

function columnTypeEmail(res, column) {
  const emailText = res[column.fieldName] || "";
  let cellElement = (
    <TableCell>
      <a className="primary-email-url" href={`mailto:${emailText}`}>
        <EllipsisText text={emailText} maxWidth={getCellTextMaxWidth(column)} />
      </a>
    </TableCell>
  );
  return cellElement;
}
function columnTypeStyledBasicWithViewIcon(dataObj, res, column) {
  const showViewIcon =
    window.location.pathname.endsWith("approvals") && res.status === 2 ? (
      <InfoOutlinedIcon
        src="/images/view_enable.png"
        height={18}
        alt="View"
        title="View"
        style={{ cursor: "pointer" }}
        onClick={() => dataObj.onReject(res)}
        data-testid={`ViewIcon${res[dataObj.uniqueKey]}`}
        className="custom-info-icon-datatable help-text-color"
      />
    ) : (
      <></>
    );

  let cellElement = (
    <TableCell>
      <Grid container alignItems="center">
        <Grid item>
          <span
            className={`styled-basic-cell-${res[
              column.fieldName
            ].toLowerCase()}`}
          >
            {res[column.fieldName]}
          </span>
        </Grid>
        {dataObj.showView && (
          <Grid item ml={1}>
            {showViewIcon}
          </Grid>
        )}
      </Grid>
    </TableCell>
  );

  return cellElement;
}
/*istanbul ignore next */
function columnTypeDate(res, column) {
  let cellElement =
    res[column.fieldName] === null ? (
      <TableCell></TableCell>
    ) : (
      <TableCell>
        <EllipsisText
          text={moment(res[column.fieldName]).format("MM/DD/YYYY")}
          maxWidth={getCellTextMaxWidth(column)}
        />
      </TableCell>
    );
  return cellElement;
}

export { DataTable, columnTypeDate, columnTypeEmail };
